import Link from '@tiptap/extension-link'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import { EditorContent, ReactRenderer, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import type { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion'
import { Bold, Code, Italic, List, ListOrdered, Quote } from 'lucide-react'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import tippy, { type GetReferenceClientRect, type Instance as TippyInstance } from 'tippy.js'
import { Markdown } from 'tiptap-markdown'
import 'tippy.js/dist/tippy.css'
import type { User } from '~/types/user.types'

interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

/**
 * Custom Mention List Component (Internal)
 */
const MentionList = forwardRef<MentionListRef, SuggestionProps<User & { label: string }>>(
  (props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0)

    const selectItem = (index: number) => {
      const item = props.items[index]
      if (item) {
        props.command(item)
      }
    }

    const upHandler = () => {
      setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length)
    }

    const downHandler = () => {
      setSelectedIndex((selectedIndex + 1) % props.items.length)
    }

    const enterHandler = () => {
      selectItem(selectedIndex)
    }

    // biome-ignore lint/correctness/useExhaustiveDependencies: this is intentional to reset the selected item when props change
    useEffect(() => setSelectedIndex(0), [props.items])

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          upHandler()
          return true
        }
        if (event.key === 'ArrowDown') {
          downHandler()
          return true
        }
        if (event.key === 'Enter') {
          enterHandler()
          return true
        }
        return false
      },
    }))

    return (
      <div className='bg-white border border-gray-100 rounded-xl shadow-2xl overflow-hidden min-w-[200px] z-50 animate-in fade-in zoom-in-95 duration-200'>
        {props.items.length > 0 ? (
          <div className='p-1'>
            {props.items.map((item: User, index: number) => (
              <button
                key={item.id}
                type='button'
                onClick={() => selectItem(index)}
                className={`w-full text-left px-3 py-2 text-sm transition-all flex items-center gap-3 rounded-lg ${
                  index === selectedIndex
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                    : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-colors ${
                    index === selectedIndex ? 'bg-white/20 text-white' : 'bg-blue-50 text-blue-600'
                  }`}
                >
                  {item.name.charAt(0)}
                </div>
                <span className='font-medium'>{item.name}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className='px-3 py-2 text-sm text-gray-500 bg-gray-50'>No results</div>
        )}
      </div>
    )
  },
)

MentionList.displayName = 'MentionList'

interface RichTextEditorProps {
  value: string
  onChange: (markdown: string) => void
  onEnter?: () => void
  placeholder?: string
  users?: User[]
}

export const RichTextEditor = ({
  value,
  onChange,
  onEnter,
  placeholder = 'Write something...',
  users = [],
}: RichTextEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      Placeholder.configure({
        placeholder,
      }),
      Link.configure({
        openOnClick: false,
      }),
      Markdown.configure({}),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
          'data-type': 'mention',
        },
        suggestion: {
          items: ({ query }) => {
            return users
              .filter((u) => u.name.toLowerCase().includes(query.toLowerCase()))
              .slice(0, 5)
              .map((u) => ({ ...u, label: u.name }))
          },
          render: () => {
            let component: ReactRenderer<MentionListRef>
            let popup: TippyInstance[]

            return {
              onStart: (props) => {
                component = new ReactRenderer(MentionList, {
                  props,
                  editor: props.editor,
                })

                if (!props.clientRect) {
                  return
                }

                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect as GetReferenceClientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                })
              },
              onUpdate(props) {
                component.updateProps(props)

                if (!props.clientRect) {
                  return
                }

                popup[0].setProps({
                  getReferenceClientRect: props.clientRect as GetReferenceClientRect,
                })
              },
              onKeyDown(props: SuggestionKeyDownProps) {
                if (props.event.key === 'Escape') {
                  popup[0].hide()
                  return true
                }
                return component.ref?.onKeyDown(props) || false
              },
              onExit() {
                popup[0].destroy()
                component.destroy()
              },
            }
          },
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const storage = editor.storage as unknown as {
        markdown?: { getMarkdown: () => string }
      }
      const markdown = storage.markdown?.getMarkdown() ?? ''
      onChange(markdown)
    },
    editorProps: {
      attributes: {
        class:
          'focus:outline-none min-h-[80px] max-h-[250px] overflow-y-auto p-4 text-sm prose prose-sm max-w-none prose-p:my-1 prose-headings:mb-2 prose-blockquote:border-l-4 prose-blockquote:border-blue-200 prose-blockquote:pl-4 prose-blockquote:italic',
      },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
          // If we want to support Enter to post, we'd need to check if the mention list is closed.
          // Tiptap's mention extension handles Enter if it's open.
          // But it's tricky to check here.
        }
        return false
      },
    },
  })

  // Synchronize value if it changes externally (e.g. after post success)
  useEffect(() => {
    if (editor && value === '') {
      const storage = editor.storage as unknown as {
        markdown?: { getMarkdown: () => string }
      }
      if (storage.markdown?.getMarkdown() !== '') {
        editor.commands.clearContent()
      }
    }
  }, [value, editor])

  return (
    <div className='border border-gray-200 rounded-xl bg-white focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-400 overflow-hidden transition-all shadow-sm'>
      <div className='bg-gray-50/50 border-b border-gray-100 px-2 py-1.5 flex gap-0.5 items-center'>
        <button
          type='button'
          onClick={() => editor?.chain().focus().toggleBold().run()}
          className={`p-1.5 rounded-md hover:bg-white hover:shadow-sm hover:text-blue-600 transition-all ${editor?.isActive('bold') ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'}`}
          title='Bold (Ctrl+B)'
        >
          <Bold size={16} strokeWidth={2.5} />
        </button>
        <button
          type='button'
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          className={`p-1.5 rounded-md hover:bg-white hover:shadow-sm hover:text-blue-600 transition-all ${editor?.isActive('italic') ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'}`}
          title='Italic (Ctrl+I)'
        >
          <Italic size={16} strokeWidth={2.5} />
        </button>
        <button
          type='button'
          onClick={() => editor?.chain().focus().toggleCode().run()}
          className={`p-1.5 rounded-md hover:bg-white hover:shadow-sm hover:text-blue-600 transition-all ${editor?.isActive('code') ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'}`}
          title='Code (Ctrl+E)'
        >
          <Code size={16} strokeWidth={2.5} />
        </button>
        <div className='w-px h-4 bg-gray-200 mx-1' />
        <button
          type='button'
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          className={`p-1.5 rounded-md hover:bg-white hover:shadow-sm hover:text-blue-600 transition-all ${editor?.isActive('bulletList') ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'}`}
          title='Bullet List'
        >
          <List size={16} strokeWidth={2.5} />
        </button>
        <button
          type='button'
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          className={`p-1.5 rounded-md hover:bg-white hover:shadow-sm hover:text-blue-600 transition-all ${editor?.isActive('orderedList') ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'}`}
          title='Ordered List'
        >
          <ListOrdered size={16} strokeWidth={2.5} />
        </button>
        <button
          type='button'
          onClick={() => editor?.chain().focus().toggleBlockquote().run()}
          className={`p-1.5 rounded-md hover:bg-white hover:shadow-sm hover:text-blue-600 transition-all ${editor?.isActive('blockquote') ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'}`}
          title='Quote'
        >
          <Quote size={16} strokeWidth={2.5} />
        </button>
        <div className='grow' />
        {onEnter && (
          <span className='text-[10px] text-gray-400 mr-2 italic font-light'>
            Shift + Enter for new line
          </span>
        )}
      </div>
      <EditorContent editor={editor} />
    </div>
  )
}
